// File: frontend/src/components/marketing/partners/PartnersSections.js
//
// The /ambassador content sections after the hero: How it works, Why join,
// Earnings explained, Referral kit preview, Rules/FAQ and the closing CTA.
// Tiers and milestone rewards live in PartnersTiers.js.
//
// ── THE ONE SENTENCE THAT MUST NEVER DRIFT ──────────────────────────────────
//
//   "You earn <rate> of PesaFii's qualifying net revenue from clients you refer."
//
// NOT "<rate> of your client's earnings". The difference is roughly sixteen
// times the money, and it is the difference between a true statement and a
// promise PesaFii cannot keep. Every rate on this page arrives from GET
// /api/partners/program/public; nothing here hard-codes it.
//
// ── COPY RULES ──────────────────────────────────────────────────────────────
//
// No em dashes in anything a reader sees. Short sentences. Only differentiators
// the current policy actually guarantees (backend/config/partnerProgram.js,
// services/partnerCommission.js, services/partnerPayouts.js).

import React, { useEffect, useRef, useState } from "react";
import {
  Box, Stack, Typography, Button, Skeleton,
  Accordion, AccordionSummary, AccordionDetails, alpha, useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";

import { gsap, useGSAP, ScrollTrigger, MQ } from "../../../utils/gsapCore";
import { referralUrl } from "../../../config/origins";
import { ugx } from "../../../pages/partner/partnerShared";
import { useReveal } from "./partnersMotion";
import { Section, SectionHeading, Surface, IconTile, CtaGroup, H2_SX, MONO } from "./PartnersPrimitives";

const SAMPLE_LINK = referralUrl("XXXX");
const SAMPLE_SHORT = SAMPLE_LINK.replace(/^https?:\/\//, "");

// "1px", not 1: in MUI sx a number <= 1 is a fraction (width: 1 means 100%).
const SR_ONLY = {
  position: "absolute", width: "1px", height: "1px", p: 0, m: "-1px",
  overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0,
};

const BUTTON_RESET = {
  appearance: "none", font: "inherit", color: "inherit", textAlign: "left",
  bgcolor: "transparent", border: 0, p: 0, m: 0, cursor: "pointer",
};

const pctText = (ratePct) => (ratePct != null ? `${ratePct}%` : null);

// ═══════════════════════════════════════════════════════════════════════════
// HOW IT WORKS
// ═══════════════════════════════════════════════════════════════════════════
//
// Four steps with an active step. Scrolling through the section moves the
// active step forward (one ScrollTrigger, no pinning, no scroll-jacking), and
// clicking or tapping a step selects it directly. Desktop reads as a row with
// a progress line; phones read as a vertical stack with a progress rail. The
// finished state (every step complete, the last one active) is the default,
// so reduced motion and failed scripts show the whole sequence.

function stepsFor(rateText) {
  return [
    {
      title: "Share your referral",
      body: "Send your link, share your code or show your QR code to a hotspot business that is not on PesaFii yet.",
      tag: SAMPLE_SHORT,
      mono: true,
    },
    {
      title: "Business joins PesaFii",
      body: "The business creates its PesaFii account through your referral. It is linked to you at signup, and that link stays with the business.",
      tag: "Linked to you at signup",
    },
    {
      title: "They generate qualifying PesaFii revenue",
      body: "The business sells internet as usual. PesaFii earns its fees as the business's customers pay.",
      tag: "PesaFii's revenue, not their sales",
    },
    {
      title: rateText ? `You receive ${rateText} Ambassador commission` : "You receive your Ambassador commission",
      body: rateText
        ? `Each time PesaFii earns from that business, your ${rateText} is posted to your Ambassador Wallet as its own line.`
        : "Each time PesaFii earns from that business, your commission is posted to your Ambassador Wallet as its own line.",
      tag: "Recurring",
    },
  ];
}

export function HowItWorks({ ratePct }) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const steps = stepsFor(pctText(ratePct));
  const last = steps.length - 1;
  const [active, setActive] = useState(last);
  const list = useRef(null);
  const root = useReveal([]);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add({ reduced: MQ.reduced, always: MQ.always }, (ctx) => {
        if (ctx.conditions.reduced || !list.current) return undefined;
        let current = -1;
        const apply = (p) => {
          const i = Math.min(last, Math.max(0, Math.floor(p * (last + 1))));
          if (i !== current) { current = i; setActive(i); }
        };
        const st = ScrollTrigger.create({
          trigger: list.current,
          start: "top 78%",
          end: "bottom 50%",
          onUpdate: (self) => apply(self.progress),
          onRefresh: (self) => apply(self.progress),
        });
        apply(st.progress);
        return () => setActive(last);
      });
    },
    { scope: root, dependencies: [] }
  );

  return (
    <Section ref={root} id="how-it-works" labelledBy="amb-how-title">
      <SectionHeading
        id="amb-how-title"
        eyebrow="How it works"
        title="From one link to recurring commission"
        subtitle="Four steps. Once the referral is recorded, your commission follows the business's activity on its own."
      />

      <Box
        component="ol"
        ref={list}
        sx={{
          listStyle: "none", m: 0, p: 0,
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(4, minmax(0, 1fr))" },
          gap: { xs: 2, md: 3 },
        }}
      >
        {steps.map((s, i) => {
          const isActive = i === active;
          const done = i < active;
          return (
            <Box
              component="li"
              key={i}
              data-reveal
              aria-current={isActive ? "step" : undefined}
              onClick={() => setActive(i)}
              sx={{
                position: "relative",
                display: "grid",
                gridTemplateColumns: { xs: "44px minmax(0, 1fr)", md: "minmax(0, 1fr)" },
                columnGap: 2,
                rowGap: 2,
                cursor: "pointer",
                minWidth: 0,
              }}
            >
              {/* Node. */}
              <Box
                aria-hidden="true"
                sx={{
                  position: "relative", zIndex: 1,
                  width: 44, height: 44, borderRadius: "50%",
                  display: "grid", placeItems: "center",
                  fontWeight: 800, fontSize: 15, fontVariantNumeric: "tabular-nums",
                  border: "2px solid",
                  borderColor: isActive || done ? "primary.main" : "var(--amb-line)",
                  bgcolor: isActive ? "primary.main" : done ? "var(--amb-accent-soft)" : "var(--amb-surface-strong)",
                  color: isActive ? "primary.contrastText" : done ? "primary.main" : "text.secondary",
                  transition: "background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease",
                }}
              >
                {i + 1}
              </Box>

              {/* Progress segment to the next step. */}
              {i < last && (
                <Box
                  aria-hidden="true"
                  sx={{
                    position: "absolute", zIndex: 0, overflow: "hidden", borderRadius: 1,
                    bgcolor: "var(--amb-track)",
                    top: { xs: 50, md: 21 },
                    bottom: { xs: -10, md: "auto" },
                    left: { xs: 21, md: 52 },
                    right: { xs: "auto", md: -16 },
                    width: { xs: 2, md: "auto" },
                    height: { xs: "auto", md: 2 },
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute", inset: 0, bgcolor: "primary.main",
                      transformOrigin: { xs: "top center", md: "left center" },
                      transform: done ? "none" : { xs: "scaleY(0)", md: "scaleX(0)" },
                      transition: "transform 0.5s ease",
                      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                    }}
                  />
                </Box>
              )}

              <Surface
                strong
                sx={{
                  p: { xs: 2.25, md: 2.5 },
                  height: "100%",
                  borderColor: isActive ? "var(--amb-accent-line)" : "var(--amb-line)",
                  boxShadow: isActive ? `0 16px 36px ${alpha(theme.palette.common.black, dark ? 0.42 : 0.08)}` : "none",
                  transition: "border-color 0.3s ease, box-shadow 0.3s ease",
                }}
              >
                <Typography variant="h3" component="h3" sx={{ fontSize: "1.08rem", fontWeight: 700, lineHeight: 1.3, color: "text.primary" }}>
                  <Box
                    component="button"
                    type="button"
                    aria-pressed={isActive}
                    onClick={(e) => { e.stopPropagation(); setActive(i); }}
                    sx={{
                      ...BUTTON_RESET,
                      borderRadius: 1,
                      "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 3 },
                    }}
                  >
                    {s.title}
                  </Box>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                  {s.body}
                </Typography>
                <Box
                  component="p"
                  sx={{
                    m: 0, mt: 1.75, display: "inline-block",
                    px: 1.25, py: 0.5, borderRadius: 1.5,
                    fontSize: 12.5, fontWeight: 600,
                    fontFamily: s.mono ? MONO : "inherit",
                    color: isActive ? "primary.main" : "text.secondary",
                    bgcolor: isActive ? "var(--amb-accent-soft)" : "var(--amb-track)",
                    transition: "background-color 0.3s ease, color 0.3s ease",
                    wordBreak: "break-all",
                  }}
                >
                  {s.tag}
                </Box>
              </Surface>
            </Box>
          );
        })}
      </Box>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// WHY JOIN
// ═══════════════════════════════════════════════════════════════════════════
//
// Four differentiators, each backed by current policy:
//   recurring        partnerCommission accrues on every qualifying event
//   itemised         one partner_ledger row per revenue event
//   one link         link, code and QR resolve to the same referral
//   no added cost    commission comes out of PesaFii's share; fees are unchanged

const REASONS = [
  {
    icon: AutorenewRoundedIcon,
    title: "Recurring, not a one-off fee",
    body: "You earn each time PesaFii earns from a business you referred, for as long as it keeps generating qualifying revenue.",
  },
  {
    icon: ReceiptLongOutlinedIcon,
    title: "Every commission itemised",
    body: "Each commission is its own line, tied to the transaction that produced it. You can trace every shilling.",
  },
  {
    icon: LinkRoundedIcon,
    title: "One referral link",
    body: "Your link, short code and QR code all carry the same referral. It is recorded the moment the business signs up.",
  },
  {
    icon: VerifiedUserOutlinedIcon,
    title: "No extra cost to the business",
    body: "Your commission comes out of PesaFii's share. The business you refer pays exactly what it would pay anyway.",
  },
];

export function WhyJoin({ ratePct }) {
  const theme = useTheme();
  const root = useReveal([ratePct]);
  const rateText = pctText(ratePct);
  const primary = theme.palette.primary.main;

  return (
    <Section ref={root} id="why-join" labelledBy="amb-why-title">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 5fr) minmax(0, 7fr)" },
          gap: { xs: 4, md: 8 },
          alignItems: "start",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <SectionHeading
            id="amb-why-title"
            align="left"
            eyebrow="Why join"
            title="Commission that keeps coming back"
            subtitle="You are paid from what PesaFii earns. That is why the commission can keep recurring without costing your client anything extra."
            sx={{ mb: 3.5 }}
          />
          <Surface
            data-reveal
            strong
            sx={{
              p: { xs: 3, md: 3.5 },
              borderRadius: 4,
              borderColor: "var(--amb-accent-line)",
              background: `radial-gradient(420px 220px at 0% 0%, ${alpha(primary, 0.14)}, transparent 70%), var(--amb-surface-strong)`,
            }}
          >
            {rateText ? (
              <Typography
                component="p"
                sx={{
                  fontSize: { xs: "3.2rem", md: "3.9rem" }, fontWeight: 800, lineHeight: 1,
                  color: "primary.main", letterSpacing: "-0.035em", fontVariantNumeric: "tabular-nums",
                }}
              >
                {rateText}
              </Typography>
            ) : (
              <Skeleton variant="text" width={130} sx={{ fontSize: "3.6rem" }} />
            )}
            <Typography component="p" sx={{ mt: 1.25, fontWeight: 800, color: "text.primary", fontSize: "1.15rem" }}>
              Recurring Ambassador commission
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75, color: "text.secondary" }}>
              Paid on PesaFii&apos;s qualifying net revenue from every business you refer. The rate is the same at every tier.
            </Typography>
          </Surface>
        </Box>

        <Box
          component="ul"
          sx={{
            listStyle: "none", m: 0, p: 0,
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
            columnGap: 5,
            pt: { md: 1 },
          }}
        >
          {REASONS.map(({ icon, title, body }) => (
            <Box
              component="li"
              key={title}
              data-reveal
              sx={{ py: { xs: 2.5, md: 3.5 }, borderTop: "1px solid", borderColor: "var(--amb-line)", minWidth: 0 }}
            >
              <IconTile icon={icon} size={42} sx={{ mb: 1.75 }} />
              <Typography variant="h3" component="h3" sx={{ fontSize: "1.12rem", fontWeight: 700, color: "text.primary" }}>
                {title}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75, color: "text.secondary" }}>
                {body}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EARNINGS EXPLAINED
// ═══════════════════════════════════════════════════════════════════════════
//
// The two example figures are ILLUSTRATIVE and labelled as such. They show a
// ratio, not a price. The commission line is computed from the API rate, so a
// policy change moves it too. The lists of what counts follow the four revenue
// sources services/partnerCommission.js accrues on, and the sources it
// explicitly excludes.

const EXAMPLE = { sales: 300000, pesafii: 18000 };

const COUNTS = [
  "PesaFii's platform fee on the business's online package payments",
  "Voucher activation fees, once PesaFii has collected them",
  "PesaFii's fee on the business's withdrawals",
];
const DOES_NOT_COUNT = [
  "The business's own sales, profit or wallet balance",
  "Mobile money network charges, which PesaFii does not keep",
  "Fees PesaFii is owed but has not collected yet",
];

function MoneyRow({ label, value, pct, strong }) {
  return (
    <Box sx={{ py: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
        <Typography variant="body2" sx={{ color: strong ? "text.primary" : "text.secondary", fontWeight: strong ? 700 : 500 }}>
          {label}
        </Typography>
        <Typography
          component="p"
          sx={{
            fontWeight: 800, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums",
            fontSize: strong ? "1.1rem" : "0.98rem",
            color: strong ? "primary.main" : "text.primary",
          }}
        >
          {value}
        </Typography>
      </Stack>
      <Box aria-hidden="true" sx={{ mt: 1, height: 10, borderRadius: 5, bgcolor: "var(--amb-track)", overflow: "hidden" }}>
        <Box
          data-reveal="grow"
          sx={{
            height: "100%", width: `${pct}%`, minWidth: pct > 0 ? 6 : 0, borderRadius: 5,
            transformOrigin: "left center",
            bgcolor: strong ? "primary.main" : pct >= 100 ? "text.secondary" : "var(--amb-accent-line)",
            opacity: pct >= 100 ? 0.35 : 1,
          }}
        />
      </Box>
    </Box>
  );
}

function RuleList({ title, items, icon: Icon, tone }) {
  return (
    <Surface data-reveal sx={{ p: { xs: 2.5, md: 3 } }}>
      <Typography variant="h3" component="h3" sx={{ fontSize: "1.05rem", fontWeight: 700, color: "text.primary", mb: 1.5 }}>
        {title}
      </Typography>
      <Stack component="ul" spacing={1.25} sx={{ listStyle: "none", m: 0, p: 0 }}>
        {items.map((line) => (
          <Box component="li" key={line} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
            <Icon aria-hidden="true" sx={{ fontSize: 20, mt: "2px", flexShrink: 0, color: tone }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>{line}</Typography>
          </Box>
        ))}
      </Stack>
    </Surface>
  );
}

export function EarningsExplained({ rate, ratePct }) {
  const root = useReveal([ratePct]);
  const rateText = pctText(ratePct);
  const commission = rate != null ? Math.round(EXAMPLE.pesafii * rate) : null;
  const pesafiiPct = (EXAMPLE.pesafii / EXAMPLE.sales) * 100;

  return (
    <Section ref={root} id="earnings" labelledBy="amb-earn-title">
      <SectionHeading
        id="amb-earn-title"
        eyebrow="Your earnings, explained"
        title="Your percentage comes from PesaFii's revenue"
        subtitle="Qualifying PesaFii revenue is what PesaFii itself earns from the business you referred. It is not the business's own sales or profit."
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 7fr) minmax(0, 5fr)" },
          gap: { xs: 2.5, md: 3.5 },
          alignItems: "stretch",
        }}
      >
        <Surface data-reveal strong sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 4 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="overline" component="p" sx={{ color: "text.secondary" }}>
              One month, one referred business
            </Typography>
            <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 1, fontSize: 11.5, fontWeight: 700, color: "text.secondary", border: "1px solid", borderColor: "var(--amb-line)" }}>
              Illustration
            </Box>
          </Stack>

          <MoneyRow label="Customers pay the hotspot business" value={ugx(EXAMPLE.sales)} pct={100} />
          <MoneyRow label="PesaFii's qualifying revenue from that business" value={ugx(EXAMPLE.pesafii)} pct={pesafiiPct} />

          <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 1, color: "text.secondary" }}>
            <ZoomInRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} />
            <Typography variant="caption" component="p">
              Zoomed in: the bar below is PesaFii&apos;s revenue only
            </Typography>
          </Stack>

          <MoneyRow
            label={rateText ? `Your ${rateText} Ambassador commission` : "Your Ambassador commission"}
            value={commission != null ? ugx(commission) : <Skeleton component="span" variant="text" width={90} sx={{ display: "inline-block" }} />}
            pct={ratePct ?? 0}
            strong
          />

          <Typography variant="caption" component="p" sx={{ mt: 1.5, color: "text.secondary" }}>
            Illustration only. Real amounts depend on the business&apos;s sales, and commission is worked out on each qualifying transaction.
          </Typography>
        </Surface>

        <Stack spacing={2.5}>
          <RuleList title="What counts" items={COUNTS} icon={CheckCircleOutlineRoundedIcon} tone="primary.main" />
          <RuleList title="What does not count" items={DOES_NOT_COUNT} icon={RemoveCircleOutlineRoundedIcon} tone="text.secondary" />
        </Stack>
      </Box>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// REFERRAL KIT PREVIEW
// ═══════════════════════════════════════════════════════════════════════════
//
// A preview, labelled as one. The link is an obvious sample (…/r/XXXX) built by
// the same referralUrl() the real kit uses, and the QR is a stylised sample
// that encodes nothing. A stranger has no code yet, so nothing here pretends
// to be a working referral.

const QR_N = 25;
const QR_PATH = (() => {
  let s = 20260910;
  const rnd = () => { s = (s * 16807) % 2147483647; return s / 2147483647; };
  const finder = (x, y) => (x < 8 && y < 8) || (x >= QR_N - 8 && y < 8) || (x < 8 && y >= QR_N - 8);
  let d = "";
  for (let y = 0; y < QR_N; y += 1) {
    for (let x = 0; x < QR_N; x += 1) {
      if (!finder(x, y) && rnd() > 0.5) d += `M${x} ${y}h1v1h-1z`;
    }
  }
  return d;
})();
const QR_FINDERS = [[0, 0], [QR_N - 7, 0], [0, QR_N - 7]];

function SampleQr() {
  const theme = useTheme();
  const ink = theme.palette.text.primary;
  return (
    <Box
      component="svg"
      viewBox={`-2 -2 ${QR_N + 4} ${QR_N + 4}`}
      role="img"
      aria-label="Sample QR code"
      shapeRendering="crispEdges"
      sx={{ width: { xs: 148, sm: 168 }, height: "auto", display: "block", borderRadius: 2, bgcolor: "background.paper" }}
    >
      <path d={QR_PATH} fill={ink} />
      {QR_FINDERS.map(([x, y]) => (
        <g key={`${x}-${y}`} fill={ink}>
          <path fillRule="evenodd" d={`M${x} ${y}h7v7h-7z M${x + 1} ${y + 1}v5h5v-5z`} />
          <rect x={x + 2} y={y + 2} width="3" height="3" />
        </g>
      ))}
    </Box>
  );
}

const KIT_RULES = [
  "The referral is recorded when the business signs up, as part of creating its account.",
  "Each business has exactly one referring Ambassador, and it stays with the business.",
  "A business that is already on PesaFii cannot be claimed later.",
];

export function ReferralKit() {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const root = useReveal([]);
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(SAMPLE_LINK);
    } catch (_) {
      // Clipboard blocked or unsupported: select the link so it can be copied
      // by hand, and do not claim a copy that did not happen.
      const node = document.getElementById("amb-sample-link");
      const sel = window.getSelection?.();
      if (node && sel && document.createRange) {
        const range = document.createRange();
        range.selectNodeContents(node);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      return;
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1800);
  };

  return (
    <Section ref={root} id="referral-kit" labelledBy="amb-kit-title">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(0, 1fr)" },
          gap: { xs: 4, md: 8 },
          alignItems: "center",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <SectionHeading
            id="amb-kit-title"
            align="left"
            eyebrow="Referral kit"
            title="One link carries your referral"
            subtitle="When you join, PesaFii issues your referral link, a short code and a QR code. All three carry the same referral, so use whichever suits the conversation."
            sx={{ mb: 3 }}
          />
          <Stack component="ul" spacing={1.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
            {KIT_RULES.map((line) => (
              <Box component="li" key={line} data-reveal sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                <CheckCircleOutlineRoundedIcon aria-hidden="true" sx={{ fontSize: 20, mt: "2px", color: "primary.main", flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>{line}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Surface
          data-reveal
          strong
          sx={{
            p: { xs: 2.5, sm: 3.5 },
            borderRadius: 4,
            boxShadow: `0 28px 64px ${alpha(theme.palette.common.black, dark ? 0.5 : 0.1)}`,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 2.5 }}>
            <Typography component="p" sx={{ fontWeight: 800, color: "text.primary" }}>Your referral kit</Typography>
            <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 1, fontSize: 11.5, fontWeight: 700, color: "primary.main", bgcolor: "var(--amb-accent-soft)" }}>
              Preview
            </Box>
          </Stack>

          <Typography component="p" sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary", mb: 0.75 }}>
            Referral link
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
            <Box
              id="amb-sample-link"
              sx={{
                flex: 1, minWidth: 0,
                px: 1.5, py: 1.25, borderRadius: 2,
                border: "1px solid", borderColor: "var(--amb-line)", bgcolor: "var(--amb-track)",
                fontFamily: MONO, fontSize: { xs: 13, sm: 14 }, color: "text.primary",
                wordBreak: "break-all",
              }}
            >
              {SAMPLE_LINK}
            </Box>
            <Button
              variant={copied ? "outlined" : "contained"}
              onClick={copy}
              startIcon={copied ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />}
              sx={{ minHeight: 44, borderRadius: 2, fontWeight: 700, whiteSpace: "nowrap", minWidth: 132 }}
            >
              {copied ? "Copied" : "Copy link"}
            </Button>
          </Stack>
          <Typography variant="caption" component="p" sx={{ mt: 1, color: "text.secondary" }}>
            Sample link. Yours is issued when you join.
          </Typography>
          <Box component="span" role="status" sx={SR_ONLY}>{copied ? "Sample link copied" : ""}</Box>

          <Box sx={{ mt: 2.5, pt: 2.5, borderTop: "1px solid", borderColor: "var(--amb-line)" }}>
            <Button
              variant="outlined"
              onClick={() => setQrOpen((v) => !v)}
              aria-expanded={qrOpen}
              aria-controls="amb-sample-qr"
              startIcon={<QrCode2RoundedIcon />}
              sx={{ minHeight: 44, borderRadius: 2 }}
            >
              {qrOpen ? "Hide QR code" : "Show QR code"}
            </Button>
            <Box id="amb-sample-qr" hidden={!qrOpen} sx={{ mt: 2 }}>
              {/* Mounted only when opened: nothing to build until someone asks. */}
              {qrOpen && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
                  <Box sx={{ p: 1.25, borderRadius: 2.5, border: "1px solid", borderColor: "var(--amb-line)", bgcolor: "background.paper" }}>
                    <SampleQr />
                  </Box>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Sample only. Your real QR code is in your Ambassador dashboard, ready to print or show on your phone.
                  </Typography>
                </Stack>
              )}
            </Box>

            <Typography component="p" sx={{ mt: 2.5, fontSize: 13, fontWeight: 600, color: "text.secondary" }}>
              Share from your dashboard
            </Typography>
            <Stack component="ul" direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ listStyle: "none", m: 0, p: 0, mt: 1 }}>
              {[
                { icon: WhatsAppIcon, label: "WhatsApp" },
                { icon: SmsOutlinedIcon, label: "SMS" },
                { icon: PrintOutlinedIcon, label: "Print QR" },
              ].map(({ icon: Icon, label }) => (
                <Box
                  component="li"
                  key={label}
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: 0.75,
                    px: 1.25, py: 0.6, borderRadius: 1.5,
                    fontSize: 13, fontWeight: 600, color: "text.secondary",
                    border: "1px solid", borderColor: "var(--amb-line)",
                  }}
                >
                  <Icon aria-hidden="true" sx={{ fontSize: 17 }} />
                  {label}
                </Box>
              ))}
            </Stack>
          </Box>
        </Surface>
      </Box>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// RULES / FAQ
// ═══════════════════════════════════════════════════════════════════════════
//
// MUI v7 wraps AccordionSummary in its own <h3> (slots.heading). The question
// text is therefore a span: an h3 inside it would nest a second heading with
// identical text and a screen reader would read every question twice.

function faqsFor({ ratePct, activeWindowDays, graceWindowDays }) {
  const pct = ratePct != null ? `${ratePct}%` : "Your commission rate";
  return [
    {
      q: "What exactly am I paid a percentage of?",
      a: `${pct} of PesaFii's qualifying net revenue from a business you referred. That is what PesaFii itself earns from the business, such as its platform fees. It is not a share of the business's own sales, and it never increases what the business pays.`,
    },
    {
      q: "How long does the commission last?",
      a: "It recurs for as long as the business keeps generating qualifying PesaFii revenue. It is not a one-off finder's fee.",
    },
    {
      q: "When is a referral recorded?",
      a: "When the business signs up through your link or code. The referral is recorded as part of creating the business's account. A business that is already on PesaFii cannot be claimed later.",
    },
    {
      q: "Do I need my own hotspot business to join?",
      a: "No. Anyone can become an Ambassador. Joining creates a personal Ambassador account and does not create a business.",
    },
    {
      q: "What if my client adds routers or changes who runs the account?",
      a: "Nothing changes for you. Each business has one referring Ambassador, and that stays with the business however many routers it adds or whoever manages it.",
    },
    {
      q: "What counts as an active client?",
      a: activeWindowDays
        ? `A client is active when it has generated qualifying PesaFii revenue in the last ${activeWindowDays} days. Your active client count sets your tier.${graceWindowDays ? ` If a client goes quiet, you keep your tier for a further ${graceWindowDays} days.` : ""}`
        : "A client is active when it has recently generated qualifying PesaFii revenue. Your active client count sets your tier, and there is a grace period if a client goes quiet.",
    },
    {
      q: "Does a higher tier pay a higher percentage?",
      a: "No. The rate is the same at every tier. Tiers unlock one-time milestone rewards and show what you have built.",
    },
    {
      q: "How do I get paid?",
      a: "Commission is posted to your Ambassador Wallet, which is separate from any business wallet. You withdraw to mobile money through the same secure flow PesaFii uses for business payouts. PesaFii adds no fee of its own to Ambassador payouts, though standard mobile money charges may apply.",
    },
    {
      q: "Can I refer my own business?",
      a: "No. You cannot refer your own business, and you cannot refer the Ambassador who referred you.",
    },
    {
      q: "What happens if an account is reviewed?",
      a: "Accounts showing signs of abuse may be suspended while we review them. Earnings and referrals are kept during a review, and a suspended Ambassador can still see them.",
    },
  ];
}

export function PartnersFAQ({ ratePct, activeWindowDays, graceWindowDays }) {
  const root = useReveal([ratePct, activeWindowDays, graceWindowDays], { y: 12 });
  const faqs = faqsFor({ ratePct, activeWindowDays, graceWindowDays });
  // Opening an answer moves everything below it. Re-measure once it settles.
  const refresh = () => ScrollTrigger.refresh();

  return (
    <Section ref={root} id="rules" labelledBy="amb-rules-title">
      <Box sx={{ maxWidth: 840, mx: "auto" }}>
        <SectionHeading id="amb-rules-title" eyebrow="Rules and questions" title="The rules, in plain words" />

        {faqs.map((f, i) => (
          <Accordion
            key={f.q}
            data-reveal
            disableGutters
            elevation={0}
            slotProps={{ transition: { onEntered: refresh, onExited: refresh } }}
            sx={{
              border: "1px solid", borderColor: "var(--amb-line)", borderRadius: "14px !important",
              bgcolor: "var(--amb-surface-strong)", mb: 1.25, overflow: "hidden",
              transition: "border-color 0.25s ease",
              "&:before": { display: "none" },
              "&.Mui-expanded": { borderColor: "var(--amb-accent-line)" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`amb-faq-content-${i}`}
              id={`amb-faq-header-${i}`}
              sx={{
                px: { xs: 2, md: 2.5 }, minHeight: 58,
                "& .MuiAccordionSummary-content": { my: 1.5 },
                "&.Mui-focusVisible": { bgcolor: "var(--amb-accent-soft)" },
              }}
            >
              <Typography component="span" sx={{ display: "block", fontWeight: 700, fontSize: "1rem", color: "text.primary" }}>
                {f.q}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 2, md: 2.5 }, pb: 2.25, pt: 0 }}>
              <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
                {f.a}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FINAL CTA
// ═══════════════════════════════════════════════════════════════════════════

export function PartnersFinalCTA({ cta, ratePct }) {
  const theme = useTheme();
  const root = useReveal([], { y: 24 });
  const primary = theme.palette.primary.main;
  const closed = Boolean(cta?.closed && !cta?.primary);
  const rateText = pctText(ratePct);

  return (
    <Section ref={root} id="join" labelledBy="amb-final-title" sx={{ pb: { xs: 8, md: 12 } }}>
      <Surface
        data-reveal
        strong
        sx={{
          borderRadius: 5,
          p: { xs: 3.5, sm: 5, md: 7 },
          textAlign: "center",
          borderColor: "var(--amb-accent-line)",
          background: `radial-gradient(640px 320px at 50% -10%, ${alpha(primary, theme.palette.mode === "dark" ? 0.2 : 0.14)}, transparent 70%), var(--amb-surface-strong)`,
        }}
      >
        <Typography id="amb-final-title" variant="h2" component="h2" sx={{ ...H2_SX, mb: 1.5 }}>
          {closed ? "Be ready when enrolment opens" : "Start earning from your network"}
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 580, mx: "auto", mb: 4 }}>
          {closed
            ? "New Ambassador sign-ups are paused for now. Existing Ambassadors can sign in as usual."
            : `Join PesaFii Ambassadors, share your link, and earn ${rateText ?? "a share"} of PesaFii's qualifying revenue from every business you bring in.`}
        </Typography>
        <CtaGroup cta={cta} align="center" />
      </Surface>
    </Section>
  );
}
